/**
 * Keyboard Phrases Editor Page
 * 
 * Allows caregivers to add, edit, delete, and reorder quick phrases
 * that appear in AAC Keyboard phrases mode
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { Check, Undo2, RotateCcw, GripVertical, Plus, Trash2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import {
  KeyboardPhrasesConfig,
  KeyboardPhrase,
  loadPhrasesConfig,
  savePhrasesConfig,
  addPhrase,
  updatePhrase,
  deletePhrase,
  reorderPhrases,
  resetPhrasesConfig,
} from "@/lib/keyboardPhrasesTypes";
import { useUnsavedEditGuard } from "@/hooks/useUnsavedEditGuard";

const PHRASES_STORAGE_KEY = "keyboardPhrases_v3";

const MAX_UNDO_HISTORY = 20;

export default function KeyboardPhrasesEditor() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  
  const [config, setConfig] = useState<KeyboardPhrasesConfig>(() => loadPhrasesConfig());
  const [undoHistory, setUndoHistory] = useState<KeyboardPhrasesConfig[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [newlyAddedPhraseId, setNewlyAddedPhraseId] = useState<string | null>(null);
  const isUndoing = useRef(false);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const { markSaved } = useUnsavedEditGuard({
    storageKeys: [PHRASES_STORAGE_KEY],
    onAfterRevert: () => setConfig(loadPhrasesConfig()),
  });

  // Save to localStorage when config changes
  useEffect(() => {
    if (!isUndoing.current) {
      savePhrasesConfig(config);
    }
  }, [config]);

  // Save to undo history before making changes
  const saveToHistory = useCallback(() => {
    setUndoHistory(prev => {
      const newHistory = [...prev, config];
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, [config]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    isUndoing.current = true;
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setConfig(previousState);
    savePhrasesConfig(previousState);
    
    setTimeout(() => {
      isUndoing.current = false;
    }, 0);
  }, [undoHistory]);

  // Handle reset to defaults
  const handleResetToDefault = () => {
    saveToHistory();
    const defaultConfig = resetPhrasesConfig();
    setConfig(defaultConfig);
  };

  // Focus new phrase input when added
  useEffect(() => {
    if (newlyAddedPhraseId) {
      const inputRef = inputRefs.current.get(newlyAddedPhraseId);
      if (inputRef) {
        inputRef.focus();
        inputRef.select();
        setNewlyAddedPhraseId(null);
      }
    }
  }, [newlyAddedPhraseId, config.phrases]);

  // Add new phrase
  const handleAddPhrase = () => {
    saveToHistory();
    const result = addPhrase(config);
    setConfig(result.config);
    setNewlyAddedPhraseId(result.newPhraseId);
  };

  // Update phrase text
  const handleUpdatePhraseText = (phraseId: string, text: string) => {
    saveToHistory();
    const updates = isRTL 
      ? { textHe: text } 
      : { textEn: text };
    const newConfig = updatePhrase(config, phraseId, updates);
    setConfig(newConfig);
  };

  // Toggle phrase enabled state
  const handleTogglePhrase = (phraseId: string, enabled: boolean) => {
    saveToHistory();
    const newConfig = updatePhrase(config, phraseId, { enabled });
    setConfig(newConfig);
  };

  // Delete phrase
  const handleDeletePhrase = (phraseId: string) => {
    saveToHistory();
    const newConfig = deletePhrase(config, phraseId);
    setConfig(newConfig);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    saveToHistory();
    const newConfig = reorderPhrases(config, draggedIndex, index);
    setConfig(newConfig);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Get sorted phrases
  const sortedPhrases = [...config.phrases].sort((a, b) => a.order - b.order);

  return (
    <MainLayout onBack={() => navigate("/keyboard-editor")}>
      <div className="flex-1 flex flex-col overflow-hidden" dir={dir}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-card/50 border-b border-border">
          <h1 className="text-xl font-bold text-foreground">
            {isRTL ? "עריכת משפטים" : "Edit Phrases"}
          </h1>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUndo}
              disabled={undoHistory.length === 0}
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {t("undo")}
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleResetToDefault}>
              <RotateCcw className="w-4 h-4 mr-2" />
              {t("resetToDefault")}
            </Button>
            
            <Button size="sm" disabled={undoHistory.length === 0} onClick={() => { markSaved(); navigate("/keyboard-editor"); }}>
              <Check className="w-4 h-4 mr-2" />
              {t("doneEditing")}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Description */}
            <p className="text-muted-foreground text-sm">
              {isRTL 
                ? "הוסף, ערוך או הסר משפטים מהירים. ניתן לשנות את הסדר ע״י גרירה."
                : "Add, edit, or remove quick phrases. You can reorder them by dragging."
              }
            </p>

            {/* Phrases List */}
            <section className="space-y-2">
              {sortedPhrases.map((phrase, index) => {
                const displayText = isRTL ? phrase.textHe : phrase.textEn;
                
                return (
                  <motion.div
                    key={phrase.id}
                    onDragOver={(e) => handleDragOver(e, index)}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl border-2 bg-card border-primary/50",
                      "transition-all duration-200",
                      draggedIndex === index && "opacity-50 scale-95"
                    )}
                    whileHover={{ scale: 1.01 }}
                  >
                    <div
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragEnd={handleDragEnd}
                      className="cursor-move"
                    >
                      <GripVertical className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                    
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary/20">
                      <MessageCircle className="w-5 h-5 text-primary" />
                    </div>
                    
                    <Input
                      ref={(el) => {
                        if (el) {
                          inputRefs.current.set(phrase.id, el);
                        } else {
                          inputRefs.current.delete(phrase.id);
                        }
                      }}
                      value={displayText}
                      onChange={(e) => handleUpdatePhraseText(phrase.id, e.target.value)}
                      placeholder={isRTL ? "הכנס כאן משפט חדש" : "Enter new phrase here"}
                      className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground placeholder:text-muted-foreground/50"
                      dir={isRTL ? "rtl" : "ltr"}
                    />
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeletePhrase(phrase.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </motion.div>
                );
              })}
            </section>

            {/* Add Phrase Button */}
            <Button
              variant="outline"
              onClick={handleAddPhrase}
              className="w-full py-6 border-dashed border-2"
            >
              <Plus className="w-5 h-5 mr-2" />
              {isRTL ? "הוסף משפט" : "Add Phrase"}
            </Button>

            {/* Preview hint */}
            <div className="p-4 rounded-xl bg-muted/30 border border-border">
              <p className="text-sm text-muted-foreground text-center">
                {isRTL 
                  ? "השינויים יופיעו במסך המקלדת במצב משפטים"
                  : "Changes will appear in the keyboard screen in phrases mode"
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
